import { createSlice, PayloadAction } from "@reduxjs/toolkit";
//constants
import {
  LAYOUT_TYPES,
  LAYOUT_SEMI_DARK,
  LAYOUT_SKIN,
  LAYOUT_MODE_TYPES,
  LAYOUT_DIRECTION,
  LAYOUT_CONTENT_WIDTH,
  LEFT_SIDEBAR_SIZE_TYPES,
  LEFT_NAVIGATION_TYPES,
  LEFT_SIDEBAR_COLOR_TYPES,
  LAYOUT_TOPBAR_THEME_TYPES
} from '../../constants/layout';
import { STORAGE_KEYS, PINNED_MODULES_CONFIG } from '../../utils/constants';
import secureStorage from '../../utils/secureStorage';

export interface Department {
  id: string;
  departmentCode: string;
  departmentName: string;
}

export interface PinnedModule {
  id: string;
  label: string;
  link: string;
  icon?: string;
  parentLabel?: string;
}

export interface LayoutState {
  layoutType: LAYOUT_TYPES.HORIZONTAL | LAYOUT_TYPES.VERTICAL;
  layoutSemiDarkType: LAYOUT_SEMI_DARK.LIGHT | LAYOUT_SEMI_DARK.DARK;
  layoutSkintype: LAYOUT_SKIN.DEFAULT | LAYOUT_SKIN.BORDERED;
  layoutModeType: LAYOUT_MODE_TYPES.LIGHTMODE | LAYOUT_MODE_TYPES.DARKMODE;
  layoutDirectionType: LAYOUT_DIRECTION.LTR | LAYOUT_DIRECTION.RTL;
  layoutContentWidthType: LAYOUT_CONTENT_WIDTH.FLUID | LAYOUT_CONTENT_WIDTH.BOXED;
  layoutSidebarSizeType: LEFT_SIDEBAR_SIZE_TYPES.DEFAULT | LEFT_SIDEBAR_SIZE_TYPES.COMPACT | LEFT_SIDEBAR_SIZE_TYPES.SMALLICON;
  layoutNavigationType: LEFT_NAVIGATION_TYPES.STICKY | LEFT_NAVIGATION_TYPES.SCROLL | LEFT_NAVIGATION_TYPES.BORDERED | LEFT_NAVIGATION_TYPES.HIDDEN;
  layoutSidebarColorType: LEFT_SIDEBAR_COLOR_TYPES.LIGHT | LEFT_SIDEBAR_COLOR_TYPES.DARK | LEFT_SIDEBAR_COLOR_TYPES.BRAND | LEFT_SIDEBAR_COLOR_TYPES.MODERN;
  layoutTopbarColorType: LAYOUT_TOPBAR_THEME_TYPES.LIGHT | LAYOUT_TOPBAR_THEME_TYPES.DARK | LAYOUT_TOPBAR_THEME_TYPES.BRAND;
  menuData: any[];
  departments: Department[];
  selectedDepartment: string | null; // department code (SFA, LOYALTY, etc.) or null for all
  pinnedModules: PinnedModule[];
  searchTriggeredModuleId: string | null; // ID of module opened via search (for auto-highlight)
}

// Load pinned modules from storage
const loadPinnedModules = (): PinnedModule[] => {
  try {
    const stored = secureStorage.getItem(STORAGE_KEYS.PINNED_MODULES);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save pinned modules to storage
const savePinnedModules = (modules: PinnedModule[]) => {
  secureStorage.setItem(STORAGE_KEYS.PINNED_MODULES, JSON.stringify(modules));
};

const initialState: LayoutState = {
  layoutType: LAYOUT_TYPES.VERTICAL,
  layoutSemiDarkType: LAYOUT_SEMI_DARK.LIGHT,
  layoutSkintype: LAYOUT_SKIN.DEFAULT,
  layoutModeType: LAYOUT_MODE_TYPES.LIGHTMODE,
  layoutDirectionType: LAYOUT_DIRECTION.LTR,
  layoutContentWidthType: LAYOUT_CONTENT_WIDTH.FLUID,
  layoutSidebarSizeType: LEFT_SIDEBAR_SIZE_TYPES.DEFAULT,
  layoutNavigationType: LEFT_NAVIGATION_TYPES.STICKY,
  layoutSidebarColorType: LEFT_SIDEBAR_COLOR_TYPES.LIGHT,
  layoutTopbarColorType: LAYOUT_TOPBAR_THEME_TYPES.LIGHT,
  menuData: [],
  departments: [],
  selectedDepartment: null, // null means show all departments
  pinnedModules: loadPinnedModules(),
  searchTriggeredModuleId: null, // null means no search-triggered navigation
};

const layoutSlice = createSlice({
  name: 'Layout',
  initialState,
  reducers: {
    changeLayoutAction: (state: any, action: any) => {
      state.layoutType = action.payload;
    },
    changeLayoutSemiDarkAction: (state: any, action: any) => {
      state.layoutSemiDarkType = action.payload;
    },
    changeSkinAction: (state: any, action: any) => {
      state.layoutSkintype = action.payload;
    },
    changeLayoutModeAction: (state: any, action: any) => {
      state.layoutModeType = action.payload;
    },
    changeDirectionAction: (state: any, action: any) => {
      state.layoutDirectionType = action.payload;
    },
    changeLayoutContentWidthAction: (state: any, action: any) => {
      state.layoutContentWidthType = action.payload;
    },
    changeLayoutSidebarSizeAction: (state: any, action: any) => {
      state.layoutSidebarSizeType = action.payload;
    },
    changeNavigationAction: (state: any, action: any) => {
      state.layoutNavigationType = action.payload;
    },
    changeLeftSidebarColorTypeAction: (state: any, action: any) => {
      state.layoutSidebarColorType = action.payload;
    },
    changeLayoutTopbarColorAction: (state: any, action: any) => {
      state.layoutTopbarColorType = action.payload;
    },
    setMenuData(state, action: PayloadAction<any[]>) {
      state.menuData = action.payload;
    },
    clearMenuData(state) {
      state.menuData = [];
    },
    setDepartments(state, action: PayloadAction<Department[]>) {
      state.departments = action.payload;
    },
    setSelectedDepartment(state, action: PayloadAction<string | null>) {
      state.selectedDepartment = action.payload;
    },
    // Pinned Modules Actions
    pinModule(state, action: PayloadAction<PinnedModule>) {
      const module = action.payload;
      const isAlreadyPinned = state.pinnedModules.some(m => m.id === module.id);

      if (!isAlreadyPinned && state.pinnedModules.length < PINNED_MODULES_CONFIG.MAX_PINNED_ITEMS) {
        state.pinnedModules.push(module);
        savePinnedModules(state.pinnedModules);
      }
    },
    unpinModule(state, action: PayloadAction<string>) {
      const moduleId = action.payload;
      state.pinnedModules = state.pinnedModules.filter(m => m.id !== moduleId);
      savePinnedModules(state.pinnedModules);
    },
    reorderPinnedModules(state, action: PayloadAction<PinnedModule[]>) {
      state.pinnedModules = action.payload;
      savePinnedModules(state.pinnedModules);
    },
    clearPinnedModules(state) {
      state.pinnedModules = [];
      savePinnedModules([]);
    },
    // Search-triggered navigation actions
    setSearchTriggeredModule(state, action: PayloadAction<string>) {
      state.searchTriggeredModuleId = action.payload;
    },
    clearSearchTriggeredModule(state) {
      state.searchTriggeredModuleId = null;
    },
  },
});

export const {
  changeLayoutAction,
  changeLayoutSemiDarkAction,
  changeSkinAction,
  changeLayoutModeAction,
  changeDirectionAction,
  changeLayoutContentWidthAction,
  changeLayoutSidebarSizeAction,
  changeNavigationAction,
  changeLeftSidebarColorTypeAction,
  changeLayoutTopbarColorAction,
  setMenuData,
  clearMenuData,
  setDepartments,
  setSelectedDepartment,
  pinModule,
  unpinModule,
  reorderPinnedModules,
  clearPinnedModules,
  setSearchTriggeredModule,
  clearSearchTriggeredModule,
} = layoutSlice.actions;
export default layoutSlice.reducer;